import { useEffect, useMemo, useState } from "react"
import { Pagination } from "react-bootstrap"

type Props<T> = {
  items: T[]
  renderItem: (item: T, index: number) => React.ReactNode
  position: "up" | "down"
  itemsPerPage?: number
  pagesPerSet?: number
}

const Paginator = <T,>({ items, renderItem, position, itemsPerPage = 5, pagesPerSet = 5 }: Props<T>) =>  {
    const [paginatedItems, setPaginatedItems] = useState<T[]>([])

    const [currentPage, setCurrentPage] = useState<number>(1) // current page out of all pages - 1 out of [1,2,3,4,5,6,7,8,9,10]
    const [pageRange, setPageRange] = useState<number[]>([]) // current page range: [1,2,3,4,5] out of [1,2,3,4,5,6,7,8,9,10]
    const [pageRangeNumber, setPageRangeNumber] = useState<number>(1) // current page range number: 1 = [1,2,3,4,5], 2 = [6,7,8,9,10]

    /** Calculate the number of pages based on the the list of items - example [1,2,3,4,5,6,7,8,9,10] */
    const totalPages = useMemo(() => {
        const totalPagesNumber = Math.ceil(items.length / itemsPerPage)
        return new Array(totalPagesNumber).fill(0).map((_, i) => i + 1)
    }, [items, itemsPerPage])

    /** Slice the original items array into 5 items for each page */
    useEffect(() => {
        const start = (currentPage - 1) * itemsPerPage
        const end = start + itemsPerPage
        setPaginatedItems(items.slice(start, end))
    }, [currentPage, items, itemsPerPage])

    /** Calculate which pages should show in one set */
    useEffect(() => {
        const start = (pageRangeNumber - 1 ) * pagesPerSet
        const end = start + pagesPerSet

        const currentPageRange = totalPages.slice(start, end)
        setPageRange(currentPageRange)
        setCurrentPage(currentPageRange[0]) // select the first page as current page out of the page set
    }, [pageRangeNumber, totalPages, pagesPerSet])

  return (
    <>
    {position === "up" && paginatedItems.map((item, index) => renderItem(item, index))}
    {totalPages.length > 1 && 
      <Pagination className="mt-3">
          {totalPages.length > pagesPerSet && 
              <Pagination.Prev disabled={pageRangeNumber === 1} onClick={() => setPageRangeNumber(prev => prev - 1)} className="me-2" />}

          {pageRange.map((pageNumber) => 
            <Pagination.Item
                className="me-2"
                onClick={() => setCurrentPage(pageNumber)}
                key={pageNumber}
                active={pageNumber === currentPage}
                data-testid={`page-${pageNumber}`}
                >
                {pageNumber}
            </Pagination.Item>
          )}

          {totalPages.length > pagesPerSet && 
              <Pagination.Next disabled={pageRangeNumber === Math.ceil(totalPages.length / pagesPerSet)} onClick={() => setPageRangeNumber(prev => prev + 1)} />}
      </Pagination>}
      {position === "down" && paginatedItems.map((item, index) => renderItem(item, index))}
    </>
  )
}

export default Paginator
